import React, { useEffect, useMemo, useState } from "react";
import api from "../../utils/api";
import {
  Box,
  Card,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Stack,
  TablePagination,
  Paper,
} from "@mui/material";

function safeJsonParse(s) {
  try {
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

const actionTypeOptions = [
  { value: "", label: "All actions" },
  { value: "election_updated", label: "Election Updated" },
  { value: "question_updated", label: "Question Updated" },
  { value: "question_created", label: "Question Created" },
  { value: "question_deleted", label: "Question Deleted" },
  { value: "option_updated", label: "Option Updated" },
  { value: "option_created", label: "Option Created" },
  { value: "option_deleted", label: "Option Deleted" },
  { value: "invite_sent", label: "Invite Sent" },
  { value: "invite_opened", label: "Invite Opened" },
  { value: "share_link_opened", label: "Share Link Opened" },
  { value: "vote_submitted", label: "Vote Submitted" },
];

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const getActionChipStyles = () => ({
  color: "#2f63ad",
  backgroundColor: "rgba(47,99,173,0.08)",
  border: "1px solid rgba(47,99,173,0.12)",
  fontWeight: 700,
});

export default function HistoryTab({ election }) {
  const electionId = election?.id;

  const [q, setQ] = useState("");
  const [actionType, setActionType] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchHistory = async () => {
    if (!electionId || electionId === "new") return;

    const res = await api.get(`/elections/${electionId}/history`, {
      params: {
        q,
        actionType,
        page: page + 1,
        pageSize,
      },
    });

    setRows(res.data?.rows || []);
    setTotal(res.data?.total || 0);
  };

  useEffect(() => {
    fetchHistory().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electionId, q, actionType, page, pageSize]);

  const handleView = (row) => {
    setSelected(row);
    setOpen(true);
  };

  const metaObj = useMemo(
    () => safeJsonParse(selected?.MetaJson),
    [selected]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          History
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Review election activity, changes, and user actions.
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: { xs: 2, md: 2.5 },
          mb: 3,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            Filters
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Search the activity log and filter by action type.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Search"
            value={q}
            onChange={(e) => {
              setPage(0);
              setQ(e.target.value);
            }}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
              },
            }}
          />

          <TextField
            label="Action"
            select
            value={actionType}
            onChange={(e) => {
              setPage(0);
              setActionType(e.target.value);
            }}
            sx={{
              minWidth: { xs: "100%", sm: 220 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
              },
            }}
          >
            {actionTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Card>

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          overflow: "hidden",
        }}
      >
        <Box sx={{ mb: 0, px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 } }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            Activity Log
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            {rows.length > 0
              ? `${total} record${total !== 1 ? "s" : ""} found`
              : "No history records found"}
          </Typography>
        </Box>

        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: 860,
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "rgba(47,99,173,0.04)",
                color: "#223142",
                fontWeight: 700,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
              },
              "& .MuiTableBody-root .MuiTableRow-root": {
                transition: "background-color 0.2s ease",
              },
              "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: "rgba(47,99,173,0.025)",
              },
              "& .MuiTableBody-root .MuiTableCell-root": {
                borderBottom: "1px solid rgba(47,99,173,0.06)",
                color: "#223142",
                py: 1.75,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Date / Time</TableCell>
                <TableCell>Action</TableCell>
                <TableCell sx={{ width: 120 }} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((r) => {
                const name =
                  r.ActorName ||
                  (r.ActorMemberCode ? `Member ${r.ActorMemberCode}` : "System");

                const when = r.CreatedAt
                  ? new Date(r.CreatedAt).toLocaleString()
                  : "";

                return (
                  <TableRow key={r.Id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                    <TableCell>{r.ActorEmail || "-"}</TableCell>
                    <TableCell>{when}</TableCell>
                    <TableCell>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Chip
                          size="small"
                          label={r.ActionType}
                          sx={getActionChipStyles()}
                        />
                        <Typography variant="body2" sx={{ color: "#223142" }}>
                          {r.ActionLabel}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleView(r)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: "10px",
                          borderColor: "rgba(47,99,173,0.18)",
                          color: "#2f63ad",
                          "&:hover": {
                            borderColor: "#2f63ad",
                            backgroundColor: "rgba(47,99,173,0.04)",
                          },
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box
                      sx={{
                        py: 5,
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ color: "#6b778c", fontWeight: 500 }}>
                        No history yet.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.01)",
          }}
        >
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPage(0);
              setPageSize(Number(e.target.value));
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Box>
      </Card>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            boxShadow: "0 18px 60px rgba(0,0,0,0.16)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          Log Details
        </DialogTitle>

        <DialogContent dividers>
          {selected && (
            <Box>
              <Stack spacing={1.25} sx={{ mb: 2.5 }}>
                <Typography>
                  <b>Action:</b> {selected.ActionLabel}
                </Typography>
                <Typography>
                  <b>Type:</b> {selected.ActionType}
                </Typography>
                <Typography>
                  <b>Actor:</b> {selected.ActorName || "-"} ({selected.ActorEmail || "-"})
                </Typography>
                <Typography>
                  <b>When:</b>{" "}
                  {selected.CreatedAt
                    ? new Date(selected.CreatedAt).toLocaleString()
                    : "-"}
                </Typography>
              </Stack>

              <Typography
                sx={{
                  fontWeight: 800,
                  color: "#223142",
                  mb: 1,
                }}
              >
                Meta
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: "#0b1020",
                  color: "#e5e7eb",
                  overflow: "auto",
                  fontSize: 13,
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {JSON.stringify(
                  metaObj ?? safeJsonParse(selected.MetaJson) ?? selected.MetaJson,
                  null,
                  2
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}